import { Outlet, useLocation } from '@tanstack/react-router'
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools'
import { AuthGate } from '@/components/auth/auth-gate'
import { AppSidebar } from '@/components/app-sidebar'

/**
 * 路由 devtools 挂不挂。
 *
 * ⚠⚠ **自动化环境下一律不挂**（`navigator.webdriver`）。由来：那个悬浮按钮在
 *   **390px** 下正好盖在「发送」上，点不动 —— 桌面上撞不到（内容居中，发送键在中间偏右，
 *   而按钮贴着屏幕最右下角），所以一直没人发现。
 * ⚠ 换个角解决不了：手机上四个角都被占（顶部是标题与「我的」，底部是输入区）。
 * ⚠ 只对自动化关掉，人开着 dev server 时行为一个字没变 —— 这不是把工具拿走，
 *   是不让它去挡被测对象。
 */
const showDevtools = import.meta.env.DEV && !navigator.webdriver

/**
 * 全局布局。路由文件只声明路由，组件一律放这里 ——
 * 一是 fast refresh 要求文件导出的是组件，二是路由文件保持薄一层好读。
 *
 * ⚠ AuthGate 包在 Outlet 外面：登录面板是覆盖层而不是页面，
 *   因为「登录后原地恢复」要求当前页面不能被卸载。
 */
export function RootLayout() {
  const pathname = useLocation({ select: (location) => location.pathname })
  if (pathname.startsWith('/meeting/') || pathname === '/share-screen') return <Outlet />
  return (
    <AuthGate>
      {/*
        模块栏 + 正文。
        ⚠⚠ 正文那一列必须带 `min-w-0`：flex 子项的默认 `min-width` 是 `auto`，
          于是里面任何一个撑不动的东西（一张宽表格、一串长 URL）都会把整列顶宽，
          进而在手机上顶出横向滚动条 —— 而「窄屏不出现横向滚动」是 W1-1 的判据，
          `e2e/layout.spec.ts` 正盯着它。
        ⚠ 模块栏挂在**根布局**而不是各页：它是应用级导航，页面之间切换时不该重建，
          窄屏抽屉的开合状态也才留得住。
      */}
      <div className="flex h-full">
        <AppSidebar />
        <div className="flex h-full min-w-0 flex-1 flex-col">
          <div className="min-h-0 flex-1">
            <Outlet />
          </div>
          <footer className="shrink-0 border-t border-neutral-100 bg-white px-4 py-2 text-center text-xs text-neutral-500">
            <a
              href="https://beian.miit.gov.cn/"
              target="_blank"
              rel="noopener noreferrer"
              className="rounded hover:text-neutral-800 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2"
            >
              粤ICP备2025483090号-2
            </a>
          </footer>
        </div>
      </div>
      {showDevtools ? <TanStackRouterDevtools position="bottom-right" /> : null}
    </AuthGate>
  )
}
